import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

type PainBlock = {
  title: string;
  lines: string[];
};

type CourseItem = {
  title: string;
  problem: string;
  solution: string;
  link: string;
};

type ScenarioItem = {
  observation: string;
  response: string;
};

export type LandingCopy = {
  hero: {
    eyebrow: string;
    title: string;
    description: string;
    reassurance: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  reality: {
    label: string;
    title: string;
    description: string;
    responseLabel: string;
    scenarios: ScenarioItem[];
  };
  path: {
    title: string;
    description: string;
    steps: Array<{ title: string; desc: string }>;
  };
  role: {
    title: string;
    description: string;
    notTitle: string;
    doesTitle: string;
    notPoints: string[];
    doesPoints: string[];
  };
  need: {
    label: string;
    title: string;
    description: string;
    pains: PainBlock[];
    closing: string;
    closingNote: string;
  };
  courses: {
    label: string;
    title: string;
    description: string;
    viewAll: string;
    fixLabel: string;
    button: string;
    items: CourseItem[];
  };
  access: {
    title: string;
    description: string;
    free: {
      title: string;
      description: string;
      price: string;
      button: string;
      features: string[];
    };
    credits: {
      tag: string;
      title: string;
      description: string;
      price: string;
      period: string;
      button: string;
      features: string[];
    };
  };
  cta: {
    title: string;
    highlight: string;
    button: string;
    note: string;
  };
};

const basePathSteps = [
  { title: "I have an idea", desc: "I know what I want to build." },
  { title: "I know what to focus on", desc: "I stop guessing." },
  { title: "I follow clear steps", desc: "I do one thing at a time." },
  { title: "I copy what works", desc: "I use proven patterns." },
  { title: "It finally works", desc: "I see real progress." },
  { title: "I have a product", desc: "I can use it and share it." },
];

export const landingCopyByLocale = defineLocaleRecord<LandingCopy>({
  en: {
    hero: {
      eyebrow: "For people with ideas, not technical skills",
      title: "You opened AI tools. Then you got stuck.",
      description:
        "You tried to start. There were too many options. No clear first step. Zipvibe removes the guesswork and gives you one clear next action.",
      reassurance: "You do not need to become technical.",
      ctaPrimary: "Show me the first step",
      ctaSecondary: "See real examples",
    },
    reality: {
      label: "What others skip",
      title: "You have seen results. You were not shown the middle.",
      description:
        "Most content jumps from idea to success. Zipvibe explains the missing steps, in the right order.",
      responseLabel: "What Zipvibe gives you",
      scenarios: [
        {
          observation: "You saw someone launch quickly.",
          response: "You get the first concrete action, not a highlight reel.",
        },
        {
          observation: "You heard that it is easy now.",
          response: "You get a simple sequence you can follow tonight.",
        },
        {
          observation: "You watched a long tutorial and still paused.",
          response: "You get only the steps that move you forward.",
        },
        {
          observation: "You saved videos and bookmarks but did nothing.",
          response: "You get one path and one clear next step.",
        },
      ],
    },
    path: {
      title: "A finite path, not a self-improvement journey",
      description: "You move from confusion to a working result in six clear steps.",
      steps: basePathSteps,
    },
    role: {
      title: "Zipvibe removes blockers. It does not teach theory.",
      description:
        "If a step does not remove a concrete problem, it does not belong in the flow.",
      notTitle: "Zipvibe is not",
      doesTitle: "Zipvibe does",
      notPoints: [
        "Not a coding school",
        "Not career education",
        "Not long technical explanations",
        "Not motivation content",
      ],
      doesPoints: [
        "We remove the guesswork",
        "We narrow it down",
        "We decide the path",
        "We tell you exactly what to do next",
        "We remove one blocker at a time",
      ],
    },
    need: {
      label: "When Zipvibe is needed",
      title: "The moment right before you give up.",
      description: "If this sounds familiar, this is the exact moment Zipvibe is for.",
      pains: [
        {
          title: "Overwhelm and too many tools",
          lines: [
            "You opened AI tools.",
            "There were too many options.",
            "Everyone recommended something different.",
            "You had no idea what to choose.",
          ],
        },
        {
          title: "No time and motivation loss",
          lines: [
            "You found a free evening.",
            "You spent 1-2 hours reading and watching.",
            "You built nothing real.",
            "Your motivation dropped.",
          ],
        },
        {
          title: "First step confusion",
          lines: [
            "Everything felt like preparation.",
            "Nothing felt like progress.",
            "There was no clear do this now step.",
          ],
        },
        {
          title: "Social comparison",
          lines: [
            "Others seemed to build things fast.",
            "Stories made it sound simple.",
            "You felt behind, not inspired.",
          ],
        },
      ],
      closing: "You do not need more tools. You need fewer decisions.",
      closingNote: "We don't add information. We remove confusion.",
    },
    courses: {
      label: "Solutions, not lessons",
      title: "Pick the blocker. Remove it.",
      description:
        "Each course starts with a real frustration and ends when that blocker is gone.",
      viewAll: "View all solutions",
      fixLabel: "Blocker removed",
      button: "Solve this now",
      items: [
        {
          title: "I don't know how to start with AI",
          problem: "You opened tools and videos, but still do not know the first real action.",
          solution:
            "We give one starting point, one order, and one small result you can finish today.",
          link: "/courses",
        },
        {
          title: "I want an app but don't know what to do first",
          problem: "You have the idea, but every next move feels random.",
          solution: "We narrow it down to a first build plan you can follow step by step.",
          link: "/courses",
        },
        {
          title: "I see AI videos everywhere but can't make one",
          problem: "You watch examples, but cannot turn your idea into a finished video.",
          solution: "We give a repeatable flow from idea to script to final output.",
          link: "/courses",
        },
        {
          title: "I have a website idea but no one sees it",
          problem: "Your offer exists, but people cannot find it or understand it quickly.",
          solution: "We help you publish a clear page so people can find and trust your offer.",
          link: "/courses",
        },
        {
          title: "I can't afford a developer",
          problem: "You need progress now, but hiring is out of reach.",
          solution: "We show a low-cost path to get a working first version online.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Start free. Choose the depth that fits what you need right now.",
      description: "No subscription pitch here. Start with free material, then go deeper only when you want more structure.",
      free: {
        title: "Starter",
        description: "For your first clear result without payment.",
        price: "$0",
        button: "Start free",
        features: [
          "One clear starting plan",
          "Free courses and examples",
          "Simple weekly checklist",
        ],
      },
      credits: {
        tag: "One-time",
        title: "Deeper path",
        description: "For a more focused path when you want clearer structure.",
        price: "$29",
        period: "one-time",
        button: "See paid access",
        features: [
          "Deeper step-by-step courses",
          "Clearer next-step checklists",
          "No recurring billing",
        ],
      },
    },
    cta: {
      title: "You do not need to become technical.",
      highlight: "You need one clear next step.",
      button: "Get my next step",
      note: "Zipvibe removes problems. It does not add noise.",
    },
  },
  fr: {
    hero: {
      eyebrow: "Pour les personnes avec des idees, pas des competences techniques",
      title: "Vous avez ouvert des outils IA. Puis vous etes reste bloque.",
      description:
        "Vous avez essaye de commencer. Trop d'options. Aucun premier pas clair. Zipvibe retire les hesitations et vous donne une action claire.",
      reassurance: "Vous n'avez pas besoin de devenir technique.",
      ctaPrimary: "Montrez-moi le premier pas",
      ctaSecondary: "Voir des exemples reels",
    },
    reality: {
      label: "Ce que les autres sautent",
      title: "Vous avez vu des resultats. Pas le milieu du processus.",
      description:
        "La plupart des contenus sautent de l'idee au resultat. Zipvibe explique les etapes manquantes, dans l'ordre.",
      responseLabel: "Ce que Zipvibe vous donne",
      scenarios: [
        {
          observation: "Vous avez vu quelqu'un lancer vite.",
          response: "Vous recevez la premiere action concrete, pas un resume brillant.",
        },
        {
          observation: "Vous avez entendu que c'est simple maintenant.",
          response: "Vous recevez une sequence simple a suivre ce soir.",
        },
        {
          observation: "Vous avez regarde un long tuto puis arrete.",
          response: "Vous recevez seulement les etapes qui font avancer.",
        },
        {
          observation: "Vous avez garde des videos et favoris sans agir.",
          response: "Vous recevez un seul chemin et une prochaine etape claire.",
        },
      ],
    },
    path: {
      title: "Un chemin fini, pas un parcours de developpement personnel",
      description: "Vous passez de la confusion a un resultat reel en six etapes claires.",
      steps: [
        { title: "J'ai une idee", desc: "Je sais ce que je veux creer." },
        { title: "Je sais quoi prioriser", desc: "J'arrete de deviner." },
        { title: "Je suis des etapes claires", desc: "Je fais une chose a la fois." },
        { title: "Je copie ce qui fonctionne", desc: "J'utilise des schemas fiables." },
        { title: "Ca fonctionne enfin", desc: "Je vois un vrai progres." },
        { title: "J'ai un produit", desc: "Je peux l'utiliser et le partager." },
      ],
    },
    role: {
      title: "Zipvibe supprime les blocages. Il n'enseigne pas la theorie.",
      description:
        "Si une etape ne retire pas un probleme concret, elle n'a pas sa place.",
      notTitle: "Zipvibe n'est pas",
      doesTitle: "Zipvibe fait",
      notPoints: [
        "Pas une ecole de code",
        "Pas une formation de carriere",
        "Pas de longues explications techniques",
        "Pas de discours de motivation",
      ],
      doesPoints: [
        "Nous supprimons les hesitations",
        "Nous reduisons les choix",
        "Nous decidons le chemin",
        "Nous vous disons exactement quoi faire ensuite",
        "Nous supprimons un blocage a la fois",
      ],
    },
    need: {
      label: "Quand Zipvibe devient necessaire",
      title: "Le moment juste avant d'abandonner.",
      description: "Si cela vous ressemble, Zipvibe est fait pour ce moment precis.",
      pains: [
        {
          title: "Trop d'outils et trop de choix",
          lines: [
            "Vous avez ouvert des outils IA.",
            "Il y avait trop d'options.",
            "Chacun recommandait quelque chose de different.",
            "Vous ne saviez pas quoi choisir.",
          ],
        },
        {
          title: "Perte de temps et de motivation",
          lines: [
            "Vous aviez une soiree libre.",
            "Vous avez passe 1-2 heures a lire et regarder.",
            "Vous n'avez rien construit de concret.",
            "La motivation est tombee.",
          ],
        },
        {
          title: "Confusion sur le premier pas",
          lines: [
            "Tout semblait etre de la preparation.",
            "Rien ne ressemblait a un progres.",
            "Aucun fais ceci maintenant clair.",
          ],
        },
        {
          title: "Comparaison sociale",
          lines: [
            "Les autres semblaient construire vite.",
            "Les histoires semblaient trop simples.",
            "Vous vous sentiez en retard, pas inspire.",
          ],
        },
      ],
      closing: "Vous n'avez pas besoin de plus d'outils. Vous avez besoin de moins de decisions.",
      closingNote: "Nous n'ajoutons pas d'information. Nous supprimons la confusion.",
    },
    courses: {
      label: "Des solutions, pas des lecons",
      title: "Choisissez le blocage. Supprimez-le.",
      description:
        "Chaque parcours commence avec une frustration reelle et se termine quand le blocage disparait.",
      viewAll: "Voir toutes les solutions",
      fixLabel: "Blocage retire",
      button: "Resoudre maintenant",
      items: [
        {
          title: "Je ne sais pas comment commencer avec l'IA",
          problem: "Vous avez ouvert des outils et des videos, mais aucun premier pas reel.",
          solution:
            "Nous donnons un point de depart, un ordre, et un petit resultat a terminer aujourd'hui.",
          link: "/courses",
        },
        {
          title: "Je veux une app mais je ne sais pas quoi faire d'abord",
          problem: "Vous avez l'idee, mais la suite semble aleatoire.",
          solution: "Nous reduisons a un premier plan clair a suivre pas a pas.",
          link: "/courses",
        },
        {
          title: "Je vois des videos IA partout mais je n'arrive pas a en faire une",
          problem: "Vous regardez des exemples, mais rien de fini ne sort.",
          solution: "Nous donnons un flux simple: idee, script, resultat final.",
          link: "/courses",
        },
        {
          title: "J'ai une idee de site mais personne ne la voit",
          problem: "Votre offre existe, mais personne ne la trouve ou ne la comprend vite.",
          solution: "Nous aidons a publier une page claire que les gens trouvent et comprennent.",
          link: "/courses",
        },
        {
          title: "Je ne peux pas payer un developpeur",
          problem: "Vous devez avancer maintenant, mais l'embauche est impossible.",
          solution: "Nous montrons un chemin a faible cout pour mettre une premiere version en ligne.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Commencez gratuit. Choisissez le niveau utile maintenant.",
      description: "Aucun discours d'abonnement. Commencez avec le gratuit, puis allez plus loin seulement si vous voulez plus de structure.",
      free: {
        title: "Demarrage",
        description: "Pour obtenir un premier resultat clair sans paiement.",
        price: "$0",
        button: "Commencer gratuitement",
        features: [
          "Un plan de depart clair",
          "Cours gratuits et exemples pratiques",
          "Checklist hebdomadaire simple",
        ],
      },
      credits: {
        tag: "Achat unique",
        title: "Parcours plus guide",
        description: "Pour avancer avec un cadre plus clair.",
        price: "$29",
        period: "achat unique",
        button: "Voir l'acces payant",
        features: [
          "Parcours plus detailles pas a pas",
          "Checklists plus claires pour la suite",
          "Un seul paiement, sans facturation recurrente",
        ],
      },
    },
    cta: {
      title: "Vous n'avez pas besoin de devenir technique.",
      highlight: "Vous avez besoin d'un prochain pas clair.",
      button: "Obtenir mon prochain pas",
      note: "Zipvibe retire les problemes. Il n'ajoute pas de bruit.",
    },
  },
  ru: {
    hero: {
      eyebrow: "Для людей с идеями, а не с техническими навыками",
      title: "Вы открыли инструменты ИИ. Потом застряли.",
      description:
        "Вы пытались начать. Слишком много вариантов. Нет понятного первого шага. Zipvibe убирает догадки и дает одно ясное действие.",
      reassurance: "Вам не нужно становиться техническим специалистом.",
      ctaPrimary: "Показать первый шаг",
      ctaSecondary: "Смотреть реальные примеры",
    },
    reality: {
      label: "Что другие пропускают",
      title: "Вы видели результат. Середину вам не показали.",
      description:
        "Большинство материалов прыгает от идеи к успеху. Zipvibe объясняет пропущенные шаги по порядку.",
      responseLabel: "Что дает Zipvibe",
      scenarios: [
        {
          observation: "Вы видели, как кто-то быстро запустился.",
          response: "Вы получаете первое конкретное действие, а не красивую нарезку.",
        },
        {
          observation: "Вы слышали, что теперь все просто.",
          response: "Вы получаете простую последовательность на сегодня.",
        },
        {
          observation: "Вы смотрели длинный урок и все равно остановились.",
          response: "Вы получаете только то, что ведет вперед.",
        },
        {
          observation: "Вы сохраняли видео и закладки, но ничего не сделали.",
          response: "Вы получаете один путь и один понятный следующий шаг.",
        },
      ],
    },
    path: {
      title: "Конечный путь, а не путь саморазвития",
      description: "От путаницы к рабочему результату за шесть понятных шагов.",
      steps: [
        { title: "У меня есть идея", desc: "Я знаю, что хочу сделать." },
        { title: "Я понимаю, на чем фокус", desc: "Я перестаю гадать." },
        { title: "Я иду по четким шагам", desc: "Я делаю по одному действию." },
        { title: "Я копирую то, что работает", desc: "Я использую проверенные шаблоны." },
        { title: "Наконец работает", desc: "Я вижу реальный прогресс." },
        { title: "У меня есть продукт", desc: "Я могу использовать его и показать." },
      ],
    },
    role: {
      title: "Zipvibe убирает блокеры. Он не учит теории.",
      description:
        "Если шаг не решает конкретную проблему, ему не место в процессе.",
      notTitle: "Zipvibe это не",
      doesTitle: "Zipvibe это",
      notPoints: [
        "Не школа программирования",
        "Не карьерное образование",
        "Не длинные технические объяснения",
        "Не мотивационные речи",
      ],
      doesPoints: [
        "Мы убираем гадание",
        "Мы сужаем выбор",
        "Мы определяем путь",
        "Мы точно говорим, что делать дальше",
        "Мы убираем по одному блокеру",
      ],
    },
    need: {
      label: "Когда нужен Zipvibe",
      title: "Момент прямо перед тем, как бросить.",
      description: "Если это знакомо, именно в этот момент нужен Zipvibe.",
      pains: [
        {
          title: "Перегруз и слишком много инструментов",
          lines: [
            "Вы открыли инструменты ИИ.",
            "Вариантов оказалось слишком много.",
            "Каждый советовал разное.",
            "Вы не знали, что выбрать.",
          ],
        },
        {
          title: "Нет времени и падает мотивация",
          lines: [
            "Вы нашли свободный вечер.",
            "Потратили 1-2 часа на чтение и видео.",
            "Ничего реального не получилось.",
            "Мотивация упала.",
          ],
        },
        {
          title: "Непонятно, с чего начать",
          lines: [
            "Все ощущалось как подготовка.",
            "Ничего не ощущалось как прогресс.",
            "Не было ясного сделай это сейчас.",
          ],
        },
        {
          title: "Сравнение с другими",
          lines: [
            "Другие как будто делают все быстро.",
            "Истории звучали слишком просто.",
            "Вы чувствовали, что отстаете, а не вдохновляетесь.",
          ],
        },
      ],
      closing: "Вам не нужно больше инструментов. Вам нужно меньше решений.",
      closingNote: "Мы не добавляем информацию. Мы убираем путаницу.",
    },
    courses: {
      label: "Решения, а не уроки",
      title: "Выберите блокер. Уберите его.",
      description:
        "Каждый курс начинается с реальной фрустрации и заканчивается, когда блокер исчезает.",
      viewAll: "Смотреть все решения",
      fixLabel: "Блокер убран",
      button: "Решить сейчас",
      items: [
        {
          title: "Я не знаю, как начать с ИИ",
          problem: "Вы открыли инструменты и видео, но не видите первого реального действия.",
          solution:
            "Мы даем одну точку старта, один порядок и маленький результат на сегодня.",
          link: "/courses",
        },
        {
          title: "Я хочу приложение, но не знаю, что делать сначала",
          problem: "Идея есть, но следующие действия кажутся случайными.",
          solution: "Мы сужаем это до первого плана сборки шаг за шагом.",
          link: "/courses",
        },
        {
          title: "Я вижу ИИ видео везде, но не могу сделать свое",
          problem: "Вы смотрите примеры, но не получаете готовый результат.",
          solution: "Мы даем повторяемый поток: идея, сценарий, финальный результат.",
          link: "/courses",
        },
        {
          title: "У меня есть идея сайта, но его никто не находит",
          problem: "Предложение есть, но люди не находят его и не понимают быстро.",
          solution: "Мы поможем опубликовать ясную страницу для поиска и доверия.",
          link: "/courses",
        },
        {
          title: "Я не могу позволить себе разработчика",
          problem: "Нужен прогресс сейчас, но найм слишком дорогой.",
          solution: "Мы показываем недорогой путь к первой рабочей версии онлайн.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Начните бесплатно. Выберите глубину, которая нужна сейчас.",
      description: "Без подписочного оффера. Сначала бесплатные материалы, дальше только если нужен более ясный формат.",
      free: {
        title: "Старт",
        description: "Для первого ясного результата без оплаты.",
        price: "$0",
        button: "Начать бесплатно",
        features: [
          "Один ясный план старта",
          "Бесплатные курсы и практические примеры",
          "Простой еженедельный чеклист",
        ],
      },
      credits: {
        tag: "Разово",
        title: "Более глубокий путь",
        description: "Для более собранного пути, когда нужна ясная структура.",
        price: "$29",
        period: "разовая покупка",
        button: "Посмотреть платный доступ",
        features: [
          "Более глубокие пошаговые курсы",
          "Более точные чеклисты следующего шага",
          "Один платеж, без регулярных списаний",
        ],
      },
    },
    cta: {
      title: "Вам не нужно становиться техническим специалистом.",
      highlight: "Вам нужен один ясный следующий шаг.",
      button: "Получить следующий шаг",
      note: "Zipvibe убирает проблемы. Он не добавляет шум.",
    },
  },
  uk: {
    hero: {
      eyebrow: "Для людей з ідеями, а не з технічними навичками",
      title: "Ви відкрили інструменти ШІ. Потім зупинилися.",
      description:
        "Ви спробували почати. Занадто багато варіантів. Немає ясного першого кроку. Zipvibe прибирає здогадки і дає одну чітку дію.",
      reassurance: "Вам не потрібно ставати технічною людиною.",
      ctaPrimary: "Показати перший крок",
      ctaSecondary: "Побачити реальні приклади",
    },
    reality: {
      label: "Що інші пропускають",
      title: "Ви бачили результати. Середину шляху вам не показали.",
      description:
        "Більшість матеріалів перестрибує від ідеї до успіху. Zipvibe пояснює пропущені кроки по порядку.",
      responseLabel: "Що дає Zipvibe",
      scenarios: [
        {
          observation: "Ви бачили, як хтось запустився дуже швидко.",
          response: "Ви отримуєте першу конкретну дію, а не лише красиву історію.",
        },
        {
          observation: "Ви чули, що тепер усе дуже просто.",
          response: "Ви отримуєте просту послідовність на сьогодні.",
        },
        {
          observation: "Ви дивилися довгий матеріал і все одно зупинилися.",
          response: "Ви отримуєте лише кроки, що ведуть вперед.",
        },
        {
          observation: "Ви зберігали відео та закладки, але нічого не зробили.",
          response: "Ви отримуєте один шлях і один чіткий наступний крок.",
        },
      ],
    },
    path: {
      title: "Кінцевий шлях, а не подорож самовдосконалення",
      description: "Від плутанини до робочого результату за шість чітких кроків.",
      steps: [
        { title: "У мене є ідея", desc: "Я знаю, що хочу зробити." },
        { title: "Я знаю, на чому фокусуватися", desc: "Я більше не вгадую." },
        { title: "Я йду за чіткими кроками", desc: "Я роблю одну дію за раз." },
        { title: "Я копіюю те, що працює", desc: "Я використовую перевірені схеми." },
        { title: "Нарешті працює", desc: "Я бачу реальний прогрес." },
        { title: "У мене є продукт", desc: "Я можу ним користуватися і показати." },
      ],
    },
    role: {
      title: "Zipvibe прибирає блокери. Він не навчає теорії.",
      description:
        "Якщо крок не прибирає конкретну проблему, він не повинен бути в процесі.",
      notTitle: "Zipvibe це не",
      doesTitle: "Zipvibe це",
      notPoints: [
        "Не школа коду",
        "Не кар'єрна освіта",
        "Не довгі технічні пояснення",
        "Не мотиваційні промови",
      ],
      doesPoints: [
        "Ми прибираємо здогадки",
        "Ми звужуємо вибір",
        "Ми визначаємо шлях",
        "Ми точно кажемо, що робити далі",
        "Ми прибираємо по одному блокеру",
      ],
    },
    need: {
      label: "Коли потрібен Zipvibe",
      title: "Момент прямо перед тим, як ви хочете кинути.",
      description: "Якщо це знайомо, саме для цього моменту є Zipvibe.",
      pains: [
        {
          title: "Перевантаження і занадто багато інструментів",
          lines: [
            "Ви відкрили інструменти ШІ.",
            "Варіантів було занадто багато.",
            "Кожен радив різне.",
            "Ви не знали, що обрати.",
          ],
        },
        {
          title: "Немає часу і падає мотивація",
          lines: [
            "Ви знайшли вільний вечір.",
            "Ви витратили 1-2 години на читання і перегляд.",
            "Ви не створили нічого реального.",
            "Мотивація впала.",
          ],
        },
        {
          title: "Плутанина з першим кроком",
          lines: [
            "Усе відчувалося як підготовка.",
            "Нічого не відчувалося як прогрес.",
            "Не було ясного зроби це зараз.",
          ],
        },
        {
          title: "Соціальне порівняння",
          lines: [
            "Інші нібито будують дуже швидко.",
            "Історії здавалися занадто простими.",
            "Ви відчували, що відстаєте, а не надихаєтеся.",
          ],
        },
      ],
      closing: "Вам не потрібно більше інструментів. Вам потрібно менше рішень.",
      closingNote: "Ми не додаємо інформацію. Ми прибираємо плутанину.",
    },
    courses: {
      label: "Рішення, а не уроки",
      title: "Оберіть блокер. Приберіть його.",
      description:
        "Кожен курс починається з реальної фрустрації і завершується, коли блокер зникає.",
      viewAll: "Дивитись усі рішення",
      fixLabel: "Блокер прибрано",
      button: "Вирішити зараз",
      items: [
        {
          title: "Я не знаю, як почати з ШІ",
          problem: "Ви відкрили інструменти і відео, але не бачите першої реальної дії.",
          solution:
            "Ми даємо одну точку старту, один порядок і маленький результат на сьогодні.",
          link: "/courses",
        },
        {
          title: "Я хочу застосунок, але не знаю, що робити спочатку",
          problem: "Ідея є, але наступні дії здаються випадковими.",
          solution: "Ми звужуємо це до першого плану, який легко виконувати крок за кроком.",
          link: "/courses",
        },
        {
          title: "Я бачу ШІ відео усюди, але не можу зробити власне",
          problem: "Ви дивитеся приклади, але не отримуєте готовий результат.",
          solution: "Ми даємо повторюваний потік: ідея, скрипт, фінальний результат.",
          link: "/courses",
        },
        {
          title: "У мене є ідея сайту, але його ніхто не бачить",
          problem: "Ваша пропозиція є, але люди не знаходять і не розуміють її швидко.",
          solution: "Ми допоможемо опублікувати зрозумілу сторінку для пошуку і довіри.",
          link: "/courses",
        },
        {
          title: "Я не можу собі дозволити розробника",
          problem: "Прогрес потрібен зараз, але найм недоступний.",
          solution: "Ми показуємо доступний шлях до першої робочої версії онлайн.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Починайте безкоштовно. Обирайте ту глибину, яка потрібна зараз.",
      description: "Без передплатного оффера. Спочатку безкоштовні матеріали, далі лише якщо потрібен чіткіший формат.",
      free: {
        title: "Старт",
        description: "Для першого зрозумілого результату без оплати.",
        price: "$0",
        button: "Почати безкоштовно",
        features: [
          "Один зрозумілий стартовий план",
          "Безкоштовні курси та практичні приклади",
          "Простий щотижневий чеклист",
        ],
      },
      credits: {
        tag: "Разово",
        title: "Глибший шлях",
        description: "Для більш зібраного шляху, коли потрібна ясніша структура.",
        price: "$29",
        period: "разова купівля",
        button: "Переглянути платний доступ",
        features: [
          "Глибші покрокові курси",
          "Чіткіші чеклісти наступного кроку",
          "Один платіж, без регулярних списань",
        ],
      },
    },
    cta: {
      title: "Вам не потрібно ставати технічною людиною.",
      highlight: "Вам потрібен один чіткий наступний крок.",
      button: "Отримати наступний крок",
      note: "Zipvibe прибирає проблеми. Він не додає шум.",
    },
  },
  de: {
    hero: {
      eyebrow: "Fur Menschen mit Ideen, nicht mit Technikwissen",
      title: "Du hast AI-Tools geoffnet. Dann bist du stecken geblieben.",
      description:
        "Du wolltest anfangen. Zu viele Optionen. Kein klarer erster Schritt. Zipvibe nimmt das Raten weg und gibt dir eine klare nachste Aktion.",
      reassurance: "Du musst nicht technisch werden.",
      ctaPrimary: "Zeig mir den ersten Schritt",
      ctaSecondary: "Echte Beispiele sehen",
    },
    reality: {
      label: "Was andere auslassen",
      title: "Du hast Ergebnisse gesehen. Der Mittelteil fehlte.",
      description:
        "Die meisten Inhalte springen von der Idee direkt zum Erfolg. Zipvibe zeigt die fehlenden Schritte in richtiger Reihenfolge.",
      responseLabel: "Was Zipvibe dir gibt",
      scenarios: [
        {
          observation: "Du hast gesehen, wie jemand sehr schnell gestartet ist.",
          response: "Du bekommst die erste konkrete Aktion, keine Highlight-Story.",
        },
        {
          observation: "Du hast gehort, dass jetzt alles leicht ist.",
          response: "Du bekommst eine einfache Reihenfolge fur heute Abend.",
        },
        {
          observation: "Du hast ein langes Tutorial gesehen und trotzdem gestoppt.",
          response: "Du bekommst nur die Schritte, die dich weiterbringen.",
        },
        {
          observation: "Du hast Videos und Lesezeichen gesammelt, aber nichts gebaut.",
          response: "Du bekommst einen Weg und einen klaren nachsten Schritt.",
        },
      ],
    },
    path: {
      title: "Ein endlicher Weg, keine Selbstoptimierungsreise",
      description: "Von Verwirrung zu einem funktionierenden Ergebnis in sechs klaren Schritten.",
      steps: [
        { title: "Ich habe eine Idee", desc: "Ich weiss, was ich bauen will." },
        { title: "Ich weiss, worauf ich mich fokussiere", desc: "Ich hore auf zu raten." },
        { title: "Ich folge klaren Schritten", desc: "Ich mache eine Sache nach der anderen." },
        { title: "Ich kopiere, was funktioniert", desc: "Ich nutze bewahrte Muster." },
        { title: "Es funktioniert endlich", desc: "Ich sehe echten Fortschritt." },
        { title: "Ich habe ein Produkt", desc: "Ich kann es nutzen und teilen." },
      ],
    },
    role: {
      title: "Zipvibe entfernt Blocker. Es lehrt keine Theorie.",
      description:
        "Wenn ein Schritt kein konkretes Problem entfernt, gehort er nicht in den Ablauf.",
      notTitle: "Zipvibe ist nicht",
      doesTitle: "Zipvibe macht",
      notPoints: [
        "Keine Programmierschule",
        "Keine Karriereausbildung",
        "Keine langen technischen Erklarungen",
        "Kein Motivationscontent",
      ],
      doesPoints: [
        "Wir nehmen das Raten weg",
        "Wir grenzen die Auswahl ein",
        "Wir legen den Weg fest",
        "Wir sagen dir genau, was du als nachstes tun sollst",
        "Wir entfernen einen Blocker nach dem anderen",
      ],
    },
    need: {
      label: "Wann Zipvibe gebraucht wird",
      title: "Der Moment direkt bevor du aufgibst.",
      description: "Wenn dir das bekannt vorkommt, ist genau jetzt Zipvibe richtig.",
      pains: [
        {
          title: "Uberforderung und zu viele Tools",
          lines: [
            "Du hast AI-Tools geoffnet.",
            "Es gab zu viele Optionen.",
            "Alle haben etwas anderes empfohlen.",
            "Du wusstest nicht, was du wahlen sollst.",
          ],
        },
        {
          title: "Keine Zeit und sinkende Motivation",
          lines: [
            "Du hattest einen freien Abend.",
            "Du hast 1-2 Stunden gelesen und geschaut.",
            "Du hast nichts Reales gebaut.",
            "Deine Motivation ist gefallen.",
          ],
        },
        {
          title: "Verwirrung beim ersten Schritt",
          lines: [
            "Alles fuhlte sich wie Vorbereitung an.",
            "Nichts fuhlte sich wie Fortschritt an.",
            "Es gab kein klares mach das jetzt.",
          ],
        },
        {
          title: "Sozialer Vergleich",
          lines: [
            "Andere schienen sehr schnell zu bauen.",
            "Geschichten klangen zu einfach.",
            "Du hast dich zuruck statt inspiriert gefuhlt.",
          ],
        },
      ],
      closing: "Du brauchst nicht mehr Tools. Du brauchst weniger Entscheidungen.",
      closingNote: "Wir fugen keine Information hinzu. Wir entfernen Verwirrung.",
    },
    courses: {
      label: "Losungen, keine Kurse",
      title: "Wahle den Blocker. Entferne ihn.",
      description:
        "Jeder Kurs startet mit einer echten Frustration und endet, wenn der Blocker weg ist.",
      viewAll: "Alle Losungen ansehen",
      fixLabel: "Blocker entfernt",
      button: "Jetzt losen",
      items: [
        {
          title: "Ich weiss nicht, wie ich mit AI anfangen soll",
          problem: "Du hast Tools und Videos geoffnet, aber keinen echten ersten Schritt.",
          solution:
            "Wir geben einen Startpunkt, eine Reihenfolge und ein kleines Ergebnis fur heute.",
          link: "/courses",
        },
        {
          title: "Ich will eine App, weiss aber nicht, was zuerst kommt",
          problem: "Die Idee ist da, aber der nachste Schritt wirkt zufallig.",
          solution: "Wir reduzieren es auf einen ersten Bauplan, den du Schritt fur Schritt folgst.",
          link: "/courses",
        },
        {
          title: "Ich sehe uberall AI-Videos, kann aber keines fertig machen",
          problem: "Du siehst Beispiele, aber bekommst kein fertiges Ergebnis.",
          solution: "Wir geben einen wiederholbaren Ablauf von Idee bis finalem Output.",
          link: "/courses",
        },
        {
          title: "Ich habe eine Website-Idee, aber niemand sieht sie",
          problem: "Dein Angebot existiert, aber niemand findet oder versteht es schnell.",
          solution: "Wir helfen dir, eine klare Seite zu veroffentlichen, die gefunden wird.",
          link: "/courses",
        },
        {
          title: "Ich kann mir keinen Entwickler leisten",
          problem: "Du brauchst jetzt Fortschritt, aber Hiring ist zu teuer.",
          solution: "Wir zeigen einen gunstigen Weg zu einer ersten funktionierenden Version.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Starte kostenlos. Waehle die Tiefe, die du jetzt brauchst.",
      description: "Kein Abo-Pitch. Starte mit kostenlosem Material und geh nur weiter, wenn du mehr Struktur willst.",
      free: {
        title: "Starter",
        description: "Fur dein erstes klares Ergebnis ohne Zahlung.",
        price: "$0",
        button: "Kostenlos starten",
        features: [
          "Ein klarer Startplan",
          "Kostenlose Kurse und praktische Beispiele",
          "Einfache Wochen-Checkliste",
        ],
      },
      credits: {
        tag: "Einmalig",
        title: "Tieferer Pfad",
        description: "Fur einen fokussierteren Weg mit klarerer Struktur.",
        price: "$29",
        period: "einmalig",
        button: "Bezahlten Zugang ansehen",
        features: [
          "Tiefere Schritt-fuer-Schritt Kurse",
          "Klarere Naechste-Schritt Checklisten",
          "Keine wiederkehrende Abrechnung",
        ],
      },
    },
    cta: {
      title: "Du musst nicht technisch werden.",
      highlight: "Du brauchst einen klaren nachsten Schritt.",
      button: "Meinen nachsten Schritt holen",
      note: "Zipvibe entfernt Probleme. Es erzeugt kein Rauschen.",
    },
  },
  es: {
    hero: {
      eyebrow: "Para personas con ideas, no con habilidades tecnicas",
      title: "Abriste herramientas de IA. Luego te quedaste bloqueado.",
      description:
        "Intentaste empezar. Demasiadas opciones. Ningun primer paso claro. Zipvibe quita la adivinacion y te da una accion clara.",
      reassurance: "No necesitas volverte tecnico.",
      ctaPrimary: "Muestrame el primer paso",
      ctaSecondary: "Ver ejemplos reales",
    },
    reality: {
      label: "Lo que otros omiten",
      title: "Viste resultados. Nadie te mostro la parte del medio.",
      description:
        "La mayoria del contenido salta de la idea al exito. Zipvibe explica los pasos faltantes en orden.",
      responseLabel: "Lo que te da Zipvibe",
      scenarios: [
        {
          observation: "Viste a alguien lanzar algo muy rapido.",
          response: "Recibes la primera accion concreta, no solo una historia bonita.",
        },
        {
          observation: "Escuchaste que ahora todo es facil.",
          response: "Recibes una secuencia simple que puedes seguir hoy.",
        },
        {
          observation: "Viste un tutorial largo y aun asi te frenaste.",
          response: "Recibes solo los pasos que te hacen avanzar.",
        },
        {
          observation: "Guardaste videos y marcadores, pero no construiste nada.",
          response: "Recibes un camino y un siguiente paso claro.",
        },
      ],
    },
    path: {
      title: "Un camino finito, no un viaje de mejora personal",
      description: "De la confusion a un resultado funcional en seis pasos claros.",
      steps: [
        { title: "Tengo una idea", desc: "Se lo que quiero construir." },
        { title: "Se en que enfocarme", desc: "Dejo de adivinar." },
        { title: "Sigo pasos claros", desc: "Hago una cosa a la vez." },
        { title: "Copio lo que funciona", desc: "Uso patrones probados." },
        { title: "Por fin funciona", desc: "Veo progreso real." },
        { title: "Tengo un producto", desc: "Puedo usarlo y compartirlo." },
      ],
    },
    role: {
      title: "Zipvibe elimina bloqueos. No ensena teoria.",
      description:
        "Si un paso no elimina un problema concreto, no pertenece al proceso.",
      notTitle: "Zipvibe no es",
      doesTitle: "Zipvibe si hace",
      notPoints: [
        "No es una escuela de programacion",
        "No es educacion de carrera",
        "No son explicaciones tecnicas largas",
        "No es contenido de motivacion",
      ],
      doesPoints: [
        "Quitamos la adivinacion",
        "Reducimos opciones",
        "Decidimos el camino",
        "Te decimos exactamente que hacer despues",
        "Quitamos un bloqueo a la vez",
      ],
    },
    need: {
      label: "Cuando necesitas Zipvibe",
      title: "El momento justo antes de rendirte.",
      description: "Si esto te suena, Zipvibe es para ese momento exacto.",
      pains: [
        {
          title: "Sobrecarga y demasiadas herramientas",
          lines: [
            "Abriste herramientas de IA.",
            "Habia demasiadas opciones.",
            "Cada persona recomendaba algo distinto.",
            "No sabias que elegir.",
          ],
        },
        {
          title: "Sin tiempo y perdida de motivacion",
          lines: [
            "Encontraste una noche libre.",
            "Pasaste 1-2 horas leyendo y viendo videos.",
            "No construiste nada real.",
            "Tu motivacion bajo.",
          ],
        },
        {
          title: "Confusion del primer paso",
          lines: [
            "Todo parecia preparacion.",
            "Nada parecia progreso.",
            "No habia un haz esto ahora claro.",
          ],
        },
        {
          title: "Comparacion social",
          lines: [
            "Otros parecian construir muy rapido.",
            "Las historias sonaban demasiado simples.",
            "Te sentias atrasado, no inspirado.",
          ],
        },
      ],
      closing: "No necesitas mas herramientas. Necesitas menos decisiones.",
      closingNote: "No agregamos informacion. Quitamos confusion.",
    },
    courses: {
      label: "Soluciones, no clases",
      title: "Elige el bloqueo. Eliminalo.",
      description:
        "Cada curso empieza con una frustracion real y termina cuando ese bloqueo desaparece.",
      viewAll: "Ver todas las soluciones",
      fixLabel: "Bloqueo eliminado",
      button: "Resolver ahora",
      items: [
        {
          title: "No se como empezar con IA",
          problem: "Abriste herramientas y videos, pero no ves la primera accion real.",
          solution: "Damos un punto de inicio, un orden y un resultado pequeno para hoy.",
          link: "/courses",
        },
        {
          title: "Quiero una app pero no se que hacer primero",
          problem: "Tienes la idea, pero cada paso siguiente parece aleatorio.",
          solution: "Lo reducimos a un primer plan claro que sigues paso a paso.",
          link: "/courses",
        },
        {
          title: "Veo videos de IA en todas partes pero no puedo hacer uno",
          problem: "Ves ejemplos, pero no logras un resultado final.",
          solution: "Damos un flujo repetible: idea, guion y resultado final.",
          link: "/courses",
        },
        {
          title: "Tengo una idea de web pero nadie la ve",
          problem: "Tu oferta existe, pero nadie la encuentra o la entiende rapido.",
          solution: "Te ayudamos a publicar una pagina clara para que te encuentren.",
          link: "/courses",
        },
        {
          title: "No puedo pagar un desarrollador",
          problem: "Necesitas avanzar ahora, pero contratar no es posible.",
          solution: "Mostramos un camino de bajo costo para una primera version funcional.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Empieza gratis. Elige la profundidad que necesitas ahora.",
      description: "Sin discurso de suscripcion. Empieza con lo gratuito y avanza solo si quieres mas estructura.",
      free: {
        title: "Inicio",
        description: "Para tu primer resultado claro sin pago.",
        price: "$0",
        button: "Empezar gratis",
        features: [
          "Un plan de inicio claro",
          "Cursos gratuitos y ejemplos practicos",
          "Checklist semanal simple",
        ],
      },
      credits: {
        tag: "Pago unico",
        title: "Ruta mas guiada",
        description: "Para avanzar con una estructura mas clara.",
        price: "$29",
        period: "pago unico",
        button: "Ver acceso de pago",
        features: [
          "Cursos mas profundos paso a paso",
          "Checklists mas claras para el siguiente paso",
          "Sin cobros recurrentes",
        ],
      },
    },
    cta: {
      title: "No necesitas volverte tecnico.",
      highlight: "Necesitas un siguiente paso claro.",
      button: "Quiero mi siguiente paso",
      note: "Zipvibe elimina problemas. No agrega ruido.",
    },
  },
  it: {
    hero: {
      eyebrow: "Per persone con idee, non con competenze tecniche",
      title: "Hai aperto strumenti AI. Poi ti sei bloccato.",
      description:
        "Hai provato a iniziare. Troppe opzioni. Nessun primo passo chiaro. Zipvibe toglie l'incertezza e ti da una sola azione chiara.",
      reassurance: "Non devi diventare tecnico.",
      ctaPrimary: "Mostrami il primo passo",
      ctaSecondary: "Vedi esempi reali",
    },
    reality: {
      label: "Quello che gli altri saltano",
      title: "Hai visto i risultati. Non hai visto la parte in mezzo.",
      description:
        "Molti contenuti saltano dall'idea al successo. Zipvibe spiega i passaggi mancanti nel giusto ordine.",
      responseLabel: "Cosa ti da Zipvibe",
      scenarios: [
        {
          observation: "Hai visto qualcuno lanciare velocemente.",
          response: "Ricevi la prima azione concreta, non solo una storia perfetta.",
        },
        {
          observation: "Hai sentito dire che ora e tutto facile.",
          response: "Ricevi una sequenza semplice da seguire stasera.",
        },
        {
          observation: "Hai guardato un tutorial lungo e ti sei fermato.",
          response: "Ricevi solo i passi che ti fanno avanzare.",
        },
        {
          observation: "Hai salvato video e link ma non hai costruito nulla.",
          response: "Ricevi un solo percorso e un prossimo passo chiaro.",
        },
      ],
    },
    path: {
      title: "Un percorso finito, non un viaggio di crescita personale",
      description: "Dalla confusione a un risultato funzionante in sei passi chiari.",
      steps: [
        { title: "Ho un'idea", desc: "So cosa voglio costruire." },
        { title: "So su cosa concentrarmi", desc: "Smetto di indovinare." },
        { title: "Seguo passi chiari", desc: "Faccio una cosa alla volta." },
        { title: "Copio cio che funziona", desc: "Uso schemi gia provati." },
        { title: "Finalmente funziona", desc: "Vedo progresso reale." },
        { title: "Ho un prodotto", desc: "Posso usarlo e condividerlo." },
      ],
    },
    role: {
      title: "Zipvibe rimuove i blocchi. Non insegna teoria.",
      description:
        "Se un passaggio non rimuove un problema concreto, non deve esistere nel flusso.",
      notTitle: "Zipvibe non e",
      doesTitle: "Zipvibe fa",
      notPoints: [
        "Non e una scuola di codice",
        "Non e formazione di carriera",
        "Non sono lunghe spiegazioni tecniche",
        "Non e motivazione",
      ],
      doesPoints: [
        "Rimuoviamo le incertezze",
        "Riduciamo le scelte",
        "Decidiamo il percorso",
        "Ti diciamo esattamente cosa fare dopo",
        "Rimuoviamo un blocco alla volta",
      ],
    },
    need: {
      label: "Quando serve Zipvibe",
      title: "Il momento subito prima di mollare.",
      description: "Se ti suona familiare, Zipvibe e pensato per questo momento preciso.",
      pains: [
        {
          title: "Sovraccarico e troppi strumenti",
          lines: [
            "Hai aperto strumenti AI.",
            "C'erano troppe opzioni.",
            "Tutti consigliavano cose diverse.",
            "Non sapevi cosa scegliere.",
          ],
        },
        {
          title: "Poco tempo e motivazione in calo",
          lines: [
            "Hai trovato una serata libera.",
            "Hai passato 1-2 ore a leggere e guardare.",
            "Non hai costruito nulla di reale.",
            "La motivazione e scesa.",
          ],
        },
        {
          title: "Confusione sul primo passo",
          lines: [
            "Tutto sembrava preparazione.",
            "Niente sembrava progresso.",
            "Mancava un chiaro fai questo adesso.",
          ],
        },
        {
          title: "Confronto sociale",
          lines: [
            "Gli altri sembravano costruire molto in fretta.",
            "Le storie sembravano troppo semplici.",
            "Ti sentivi indietro, non ispirato.",
          ],
        },
      ],
      closing: "Non ti servono piu strumenti. Ti servono meno decisioni.",
      closingNote: "Non aggiungiamo informazioni. Rimuoviamo confusione.",
    },
    courses: {
      label: "Soluzioni, non lezioni",
      title: "Scegli il blocco. Rimuovilo.",
      description:
        "Ogni corso parte da una frustrazione reale e finisce quando quel blocco sparisce.",
      viewAll: "Vedi tutte le soluzioni",
      fixLabel: "Blocco rimosso",
      button: "Risolvi ora",
      items: [
        {
          title: "Non so come iniziare con AI",
          problem: "Hai aperto strumenti e video, ma non vedi la prima azione reale.",
          solution: "Ti diamo un punto di partenza, un ordine e un piccolo risultato oggi.",
          link: "/courses",
        },
        {
          title: "Voglio un'app ma non so cosa fare per primo",
          problem: "Hai l'idea, ma ogni passo successivo sembra casuale.",
          solution: "La riduciamo a un primo piano chiaro da seguire passo passo.",
          link: "/courses",
        },
        {
          title: "Vedo video AI ovunque ma non riesco a farne uno",
          problem: "Guardi esempi, ma non arrivi a un risultato finito.",
          solution: "Ti diamo un flusso ripetibile: idea, script, risultato finale.",
          link: "/courses",
        },
        {
          title: "Ho un'idea di sito ma nessuno la vede",
          problem: "La tua offerta esiste, ma nessuno la trova o la capisce in fretta.",
          solution: "Ti aiutiamo a pubblicare una pagina chiara che le persone trovano.",
          link: "/courses",
        },
        {
          title: "Non posso permettermi uno sviluppatore",
          problem: "Ti serve progresso adesso, ma assumere non e possibile.",
          solution: "Mostriamo un percorso a basso costo per una prima versione funzionante.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Inizia gratis. Scegli la profondita che ti serve adesso.",
      description: "Nessun discorso da abbonamento. Parti dal gratuito e vai oltre solo se vuoi piu struttura.",
      free: {
        title: "Base",
        description: "Per il tuo primo risultato chiaro senza pagamento.",
        price: "$0",
        button: "Inizia gratis",
        features: [
          "Un piano iniziale chiaro",
          "Corsi gratuiti ed esempi pratici",
          "Checklist settimanale semplice",
        ],
      },
      credits: {
        tag: "Una tantum",
        title: "Percorso piu guidato",
        description: "Per avanzare con una struttura piu chiara.",
        price: "$29",
        period: "una tantum",
        button: "Vedi accesso a pagamento",
        features: [
          "Corsi piu profondi passo dopo passo",
          "Checklist piu chiare per il prossimo passo",
          "Nessuna fatturazione ricorrente",
        ],
      },
    },
    cta: {
      title: "Non devi diventare tecnico.",
      highlight: "Ti serve un prossimo passo chiaro.",
      button: "Voglio il mio prossimo passo",
      note: "Zipvibe rimuove problemi. Non aggiunge rumore.",
    },
  },
  pt: {
    hero: {
      eyebrow: "Para pessoas com ideias, nao com habilidades tecnicas",
      title: "Voce abriu ferramentas de IA. Depois travou.",
      description:
        "Voce tentou comecar. Opcoes demais. Nenhum primeiro passo claro. O Zipvibe elimina as duvidas e te da uma acao clara.",
      reassurance: "Voce nao precisa se tornar tecnico.",
      ctaPrimary: "Me mostre o primeiro passo",
      ctaSecondary: "Ver exemplos reais",
    },
    reality: {
      label: "O que os outros pulam",
      title: "Voce viu resultados. Ninguem te mostrou o meio do caminho.",
      description:
        "A maioria dos conteudos pula da ideia para o sucesso. O Zipvibe explica os passos que faltam, na ordem certa.",
      responseLabel: "O que o Zipvibe te da",
      scenarios: [
        {
          observation: "Voce viu alguem lancar algo muito rapido.",
          response: "Voce recebe a primeira acao concreta, nao apenas uma historia bonita.",
        },
        {
          observation: "Voce ouviu dizer que agora tudo e facil.",
          response: "Voce recebe uma sequencia simples para seguir hoje.",
        },
        {
          observation: "Voce assistiu um tutorial longo e mesmo assim parou.",
          response: "Voce recebe apenas os passos que te fazem avancar.",
        },
        {
          observation: "Voce salvou videos e favoritos, mas nao construiu nada.",
          response: "Voce recebe um caminho e um proximo passo claro.",
        },
      ],
    },
    path: {
      title: "Um caminho finito, nao uma jornada de autodesenvolvimento",
      description: "Da confusao a um resultado funcional em seis passos claros.",
      steps: [
        { title: "Eu tenho uma ideia", desc: "Sei o que quero construir." },
        { title: "Sei no que focar", desc: "Paro de adivinhar." },
        { title: "Sigo passos claros", desc: "Faco uma coisa de cada vez." },
        { title: "Copio o que funciona", desc: "Uso padroes comprovados." },
        { title: "Finalmente funciona", desc: "Vejo progresso real." },
        { title: "Tenho um produto", desc: "Posso usar e compartilhar." },
      ],
    },
    role: {
      title: "O Zipvibe remove bloqueios. Nao ensina teoria.",
      description:
        "Se um passo nao remove um problema concreto, ele nao pertence ao fluxo.",
      notTitle: "Zipvibe nao e",
      doesTitle: "Zipvibe faz",
      notPoints: [
        "Nao e escola de programacao",
        "Nao e educacao de carreira",
        "Nao sao explicacoes tecnicas longas",
        "Nao e conteudo motivacional",
      ],
      doesPoints: [
        "Eliminamos as duvidas",
        "Reduzimos as opcoes",
        "Decidimos o caminho",
        "Dizemos exatamente o que fazer a seguir",
        "Removemos um bloqueio de cada vez",
      ],
    },
    need: {
      label: "Quando o Zipvibe e necessario",
      title: "O momento logo antes de desistir.",
      description: "Se isso parece familiar, o Zipvibe foi feito para esse momento.",
      pains: [
        {
          title: "Sobrecarga e ferramentas demais",
          lines: [
            "Voce abriu ferramentas de IA.",
            "Havia opcoes demais.",
            "Cada pessoa recomendava algo diferente.",
            "Voce nao sabia o que escolher.",
          ],
        },
        {
          title: "Sem tempo e perda de motivacao",
          lines: [
            "Voce encontrou uma noite livre.",
            "Passou 1-2 horas lendo e assistindo.",
            "Nao construiu nada real.",
            "Sua motivacao caiu.",
          ],
        },
        {
          title: "Confusao no primeiro passo",
          lines: [
            "Tudo parecia preparacao.",
            "Nada parecia progresso.",
            "Nao havia um faca isso agora claro.",
          ],
        },
        {
          title: "Comparacao social",
          lines: [
            "Outros pareciam construir muito rapido.",
            "As historias pareciam simples demais.",
            "Voce se sentia atrasado, nao inspirado.",
          ],
        },
      ],
      closing: "Voce nao precisa de mais ferramentas. Precisa de menos decisoes.",
      closingNote: "Nos nao adicionamos informacao. Removemos a confusao.",
    },
    courses: {
      label: "Solucoes, nao aulas",
      title: "Escolha o bloqueio. Remova-o.",
      description:
        "Cada curso comeca com uma frustracao real e termina quando esse bloqueio desaparece.",
      viewAll: "Ver todas as solucoes",
      fixLabel: "Bloqueio removido",
      button: "Resolver agora",
      items: [
        {
          title: "Nao sei como comecar com IA",
          problem: "Voce abriu ferramentas e videos, mas nao ve a primeira acao real.",
          solution:
            "Damos um ponto de partida, uma ordem e um pequeno resultado para terminar hoje.",
          link: "/courses",
        },
        {
          title: "Quero um app mas nao sei o que fazer primeiro",
          problem: "Voce tem a ideia, mas cada proximo passo parece aleatorio.",
          solution: "Reduzimos a um primeiro plano claro que voce segue passo a passo.",
          link: "/courses",
        },
        {
          title: "Vejo videos de IA por toda parte mas nao consigo fazer um",
          problem: "Voce assiste exemplos, mas nao chega a um resultado final.",
          solution: "Damos um fluxo repetivel: ideia, roteiro e resultado final.",
          link: "/courses",
        },
        {
          title: "Tenho uma ideia de site mas ninguem o ve",
          problem: "Sua oferta existe, mas ninguem a encontra ou entende rapidamente.",
          solution: "Ajudamos voce a publicar uma pagina clara para que as pessoas encontrem e confiem.",
          link: "/courses",
        },
        {
          title: "Nao posso pagar um desenvolvedor",
          problem: "Voce precisa de progresso agora, mas contratar esta fora de alcance.",
          solution: "Mostramos um caminho de baixo custo para colocar uma primeira versao no ar.",
          link: "/courses",
        },
      ],
    },
    access: {
      title: "Comece gratis. Escolha a profundidade que voce precisa agora.",
      description: "Sem papo de assinatura. Comece com o material gratuito e va mais fundo so quando quiser mais estrutura.",
      free: {
        title: "Inicio",
        description: "Para seu primeiro resultado claro sem pagar nada.",
        price: "$0",
        button: "Comecar gratis",
        features: [
          "Um plano inicial claro",
          "Cursos gratuitos e exemplos praticos",
          "Checklist semanal simples",
        ],
      },
      credits: {
        tag: "Pagamento unico",
        title: "Caminho mais guiado",
        description: "Para avancar com uma estrutura mais clara.",
        price: "$29",
        period: "pagamento unico",
        button: "Ver acesso pago",
        features: [
          "Cursos mais profundos passo a passo",
          "Checklists mais claras para o proximo passo",
          "Sem cobranca recorrente",
        ],
      },
    },
    cta: {
      title: "Voce nao precisa se tornar tecnico.",
      highlight: "Voce precisa de um proximo passo claro.",
      button: "Quero meu proximo passo",
      note: "O Zipvibe remove problemas. Nao adiciona ruido.",
    },
  },
});

export function getLandingCopy(lang: Locale): LandingCopy {
  return landingCopyByLocale[lang];
}
